import { useSceneEvents } from "@/components/common/scene-events-context";
import { useLiveDraftTransitionStore } from "@/data-preparation-tool/live-draft-transition-store";
import {
  useCameraPredefinedViews,
  useResetPredefinedViewWhenCameraMoves,
} from "@/hooks/use-camera-predefined-views";
import { useManageProjectionSwitch } from "@/hooks/use-manage-projection-switch";
import { CameraView, CameraViewType, SetCameraView } from "@/utils/cam-view";
import { useTypedEvent } from "@faro-lotv/app-component-toolbox";
import { WalkOrbitControls } from "@faro-lotv/lotv";
import { useCallback } from "react";
import { FlyWalkRenderables } from "./data-dispatch/flywalk-renderables";

type SetControls = (controls: WalkOrbitControls | null) => void;
type SetCameraProjection = (projection: CameraViewType) => void;

/**
 * This hook provides functionalities required by the current overview mode, such as camera predefined view,
 * camera perspective/ortho switch, and reactions to go-to-annotation events.
 *
 * @param renderables Current renderables in the scene
 * @param setCameraView Callback issued to start a camera animation to a predefined view
 * @param setCameraProjection Callback issued to change the current camera projection (perspective or orthographic)
 * @returns Callback to set the camera controls
 */
export function useManageCameraControls(
  renderables: FlyWalkRenderables,
  setCameraView: SetCameraView,
  setCameraProjection: SetCameraProjection,
): SetControls {
  const clippingBox = useCameraPredefinedViews(
    setCameraView,
    renderables.areaBox,
    renderables.allModelsBox,
  );
  const {
    setControls: setControlsForSwitch,
    setAllowOrthoCameraInsideModelOnce,
  } = useManageProjectionSwitch(clippingBox, setCameraView);

  const onCameraViewChanged = useCallback(
    (v: CameraView) => {
      if (v.type === CameraViewType.orthographic) {
        // If we enter here, an annotation wants to restore an orthographic camera.
        // Only in this case, we allow the orthographic camera to be inside the model.
        setAllowOrthoCameraInsideModelOnce();
      }
      setCameraProjection(v.type);
      setCameraView(v);
    },
    [setCameraProjection, setCameraView, setAllowOrthoCameraInsideModelOnce],
  );

  const sceneEvents = useSceneEvents();
  useTypedEvent(sceneEvents.setCameraView, onCameraViewChanged);

  const setControlsForResetView = useResetPredefinedViewWhenCameraMoves();

  const { setOrbitControls: setInitialDraftControls } =
    useLiveDraftTransitionStore();

  const setControls = useCallback(
    (controls: WalkOrbitControls | null) => {
      setControlsForSwitch(controls);
      setControlsForResetView(controls);
      if (controls) {
        setInitialDraftControls(controls);
      }
    },
    [setControlsForSwitch, setControlsForResetView, setInitialDraftControls],
  );

  return setControls;
}
